import React from 'react';
import { Box, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

export type KpiBarItem = {
  key: string;
  icon: React.ReactNode;
  iconColor: string;
  value: string;
  valueColor?: string;
  label: string;
};

export type KpisBarProps = {
  items: KpiBarItem[];
  ariaLabel: string;
};

const KpisBar: React.FC<KpisBarProps> = ({ items, ariaLabel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        mb: 3,
        py: { xs: 1.25, sm: 1.5 },
        px: { xs: 1.5, sm: 2 },
        bgcolor: '#fff',
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        overflowX: 'auto',
      }}
      role="status"
      aria-label={ariaLabel}
    >
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'stretch' : 'center'}
        divider={
          <Divider
            orientation={isMobile ? 'horizontal' : 'vertical'}
            flexItem
            sx={{ borderColor: '#e0e0e0' }}
          />
        }
        spacing={0}
      >
        {items.map((item) => (
          <Stack
            key={item.key}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              flex: 1,
              px: { xs: 0.5, sm: 1.5 },
              py: { xs: 1, sm: 0.5 },
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                color: item.iconColor,
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              {item.icon}
            </Box>
            <Stack
              direction="row"
              alignItems="baseline"
              spacing={0.75}
              sx={{ minWidth: 0, flexWrap: 'wrap' }}
            >
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  lineHeight: 1.2,
                  color: item.valueColor ?? '#424242',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.value}
              </Typography>
              <Typography
                component="span"
                variant="body2"
                sx={{ color: '#9e9e9e', fontWeight: 400, whiteSpace: 'nowrap' }}
              >
                {item.label}
              </Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default KpisBar;
